/**
 * Один стор фильтров на всё приложение.
 *
 * Карточки подписаны на него и перезапрашиваются сами; чат в него пишет.
 * Отдельного состояния у чата нет намеренно — иначе «дашборд показывает одно,
 * чат говорит другое» стало бы возможным состоянием экрана.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

import type { FilterState, Me } from "../api/types";
import { DEFAULT_STATE, stateFromQuery, stateToQuery } from "./filters";

type Store = {
  state: FilterState;
  tool: string;
  me: Me | null;
  patch: (changes: Partial<FilterState>) => void;
  replace: (next: FilterState) => void;
  setTool: (tool: string) => void;
  reset: () => void;
};

const StoreContext = createContext<Store | null>(null);

export function StoreProvider({ me, children }: { me: Me | null; children: ReactNode }) {
  const [state, setState] = useState<FilterState>(() => stateFromQuery(window.location.search));
  const [tool, setTool] = useState("count_episodes");

  // Адрес строки следует за фильтрами: ссылку на вид можно переслать.
  useEffect(() => {
    const query = stateToQuery(state);
    window.history.replaceState(null, "", query ? `?${query}` : window.location.pathname);
  }, [state]);

  const patch = useCallback((changes: Partial<FilterState>) => {
    setState((current) => ({ ...current, ...changes }));
  }, []);

  const value = useMemo<Store>(
    () => ({
      state,
      tool,
      me,
      patch,
      replace: setState,
      setTool,
      reset: () => setState(DEFAULT_STATE),
    }),
    [state, tool, me, patch],
  );

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useStore(): Store {
  const store = useContext(StoreContext);
  if (!store) throw new Error("useStore вне StoreProvider");
  return store;
}
